<template>
  <modal
    :show="isVisible"
    :title="t('Detalhes da transação')"
    elem-id="modal_staff_transaction"
    size="compactDesktop"
    :close-label="t('Fechar')"
    :save-label="canMarkRefunded ? t('Marcar como estornado') : ''"
    save-icon="pi pi-undo"
    save-test-id="staff-transaction-mark-refunded"
    :save-fn="handleMarkRefunded"
    :save-disabled-fn="() => marking"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="staff-transaction-modal">
        <compact-data-table :value="detailRows" @copied="onCopied" @copy-failed="onCopyFailed" />

        <!-- Simulador de webhook — só em ambiente de dev, só para Full, só numa
             cobrança pendente de um gateway simulável. Ver canSimulate(). -->
        <box v-if="canSimulate" class="simulate-box" data-testid="transaction-simulate-box">
          <template #header>{{ t('Simular retorno do gateway') }}</template>

          <p class="simulate-description">
            {{ simulateDescription }}
          </p>

          <div class="simulate-actions">
            <Button
              :label="t('Confirmar pagamento')"
              icon="pi pi-check"
              severity="success"
              :loading="isSimulating"
              data-testid="transaction-simulate-confirm"
              @click="handleSimulate('confirm')"
            />
            <Button
              :label="t('Recusar pagamento')"
              icon="pi pi-times"
              severity="danger"
              :loading="isSimulating"
              data-testid="transaction-simulate-reject"
              @click="handleSimulate('reject')"
            />
          </div>

          <inline-message
            v-if="simulateResult"
            :severity="simulateResult.severity"
            data-testid="transaction-simulate-result"
          >
            {{ simulateResult.message }}
          </inline-message>
        </box>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import { useAdminTransactions } from '../../../composables/useAdminTransactions'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'
import permissionsService from '../../../services/permissionsService'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import type { AttemptDisplayRow } from './StaffTransactionsTable.vue'
import CompactDataTable from '../../../grid/CompactDataTable.vue'

const { t, n } = useI18n()
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter()
const { markRefunded } = useAdminTransactions()
const {
  featureMap,
  loadMap: loadGatewayMap,
  fireFakeTrigger,
  fireAsaasTrigger,
} = usePaymentGatewayRegistry()

const isVisible = ref(false)
const currentRow = ref<AttemptDisplayRow | null>(null)
const marking = ref(false)
const isSimulating = ref(false)
const simulateResult = ref<{ severity: 'success' | 'warn' | 'error'; message: string } | null>(null)

const kindLabelMap: Record<string, string> = {
  cycle: t('Mensalidade'),
  charge: t('Mensalidade'),
  setup_fee: t('Taxa de implantação'),
  credit_purchase: t('Créditos IA'),
}

const statusLabelMap: Record<string, string> = {
  pending: t('Pendente'),
  confirmed: t('Confirmada'),
  failed: t('Falhou'),
  overdue: t('Vencida'),
  expired: t('Abandonada'),
  superseded: t('Substituída'),
  // M3c (D5) — money that landed after the cut, and its only exit. Same map as
  // StaffTransactionsTable's badge: a raw English code must never reach a
  // user-facing field (§5, skill billing-staff-console).
  refund_pending: t('Estorno pendente'),
  refunded: t('Estornada'),
}

const detailRows = computed(() => {
  const row = currentRow.value
  if (!row) return []

  const kindLabel = row.kind ? (kindLabelMap[row.kind] ?? row.kind) : '—'
  const statusLabel = row.status ? (statusLabelMap[row.status] ?? row.status) : '—'
  let amountDisplay = row.amount_display
  if (!amountDisplay && (row as unknown as Record<string, unknown>).amount_cents != null) {
    const rawCents = Number((row as unknown as Record<string, unknown>).amount_cents)
    const currency = (row.currency && /^[A-Z]{3}$/i.test(row.currency)) ? row.currency.toUpperCase() : 'BRL'
    try {
      amountDisplay = n(
        rawCents / currencyFactor('pt-BR', currency),
        { style: 'currency', currency },
      )
    } catch {
      amountDisplay = `${rawCents / 100}`
    }
  }

  const rawCreatedAt = (row as unknown as Record<string, unknown>).created_at as string | undefined
  const rawPaidAt = (row as unknown as Record<string, unknown>).paid_at as string | undefined
  const createdAt = row.created_at_display ?? (rawCreatedAt ? formatDateTime(fromApiDateTimeFormat(rawCreatedAt)) : '—')
  const paidAt = row.paid_at_display ?? (rawPaidAt ? formatDateTime(fromApiDateTimeFormat(rawPaidAt)) : '—')

  return [
    { prop: t('ID'), value: row.id ?? '—' },
    { prop: t('Franquia'), value: row.franchise_name ?? row.franquia_id ?? '—' },
    { prop: t('Tipo'), value: kindLabel },
    { prop: t('Valor'), value: amountDisplay || '—' },
    { prop: t('Situação'), value: statusLabel },
    { prop: t('Provedor'), value: row.provider ?? '—' },
    // copyable: staff cross-references this id in the provider's own dashboard
    // (Stripe/ASAAS) — hand-transcribing a 27-char id is how typos happen. The
    // button only renders when there IS an id ('—' stays plain).
    { prop: t('ID da cobrança'), value: row.provider_charge_id ?? '—', copyable: true, testId: 'staff-transaction-charge-id' },
    { prop: t('Data da tentativa'), value: createdAt },
    { prop: t('Pago em'), value: paidAt },
  ]
})


// M3c (D5): the action exists only for a charge that is actually awaiting a
// refund — the backend refuses any other status by name, and offering a button
// whose only possible answer is "no" is how staff learn to distrust the screen.
// No group check here: /config/transacoes is requiresGroups:['Full'] at the
// router, and the endpoint gates Full again server-side (assertFullOnlyAccess).
const canMarkRefunded = computed(() => currentRow.value?.status === 'refund_pending')

// ── Simulador de webhook (dev) ──────────────────────────────────────────────
//
// ANTES ISTO SÓ EXISTIA EM /config/payment-gateways, e para simular o retorno
// de UMA transação era preciso sair da tela de transações, ir até o painel do
// gateway e digitar o charge id à mão. O gesto pertence à transação, então o
// botão passa a morar aqui — onde a cobrança já está identificada e o id não
// precisa ser transcrito.
//
// Provedores simuláveis e o evento que cada um entende. GatewayTest fala o
// nosso vocabulário canônico; ASAAS fala o dele, porque o payload sintético é
// normalizado pelo MESMO adapter que lê uma entrega real (ver
// AsaasWebhookPayloadBuilder).
const SIMULABLE_PROVIDERS = ['asaas', 'gatewaytest'] as const
type SimulableProvider = (typeof SIMULABLE_PROVIDERS)[number]

const isFull = computed<boolean>(() => permissionsService.isUserInGroups(['Full']))

const rowProvider = computed<SimulableProvider | null>(() => {
  const provider = currentRow.value?.provider
  return SIMULABLE_PROVIDERS.includes(provider as SimulableProvider)
    ? (provider as SimulableProvider)
    : null
})

/**
 * O SINAL DE AMBIENTE É O FLAG `enabled`, NÃO A PRESENÇA NO MAPA. O backend
 * semeia uma linha para todo provedor em todo ambiente — o que muda é o valor,
 * derivado de AWRENV_SISTEMAAPI_GATEWAYTEST_ENABLED, que produção nunca define.
 * `import.meta.env.DEV` não serviria: todo deploy (hml incluso) é buildado com
 * `--mode production`, e hml é justamente onde o simulador deve funcionar.
 *
 * Isto duplica o gate do servidor de propósito: o endpoint responde 404 fora de
 * dev de qualquer forma. Aqui é só para não desenhar um botão que não pode
 * funcionar.
 */
const providerIsSimulableHere = computed<boolean>(() => {
  const provider = rowProvider.value
  if (!provider || !featureMap.value) return false
  return Object.values(featureMap.value).some(
    providerMap => providerMap[provider]?.enabled === true,
  )
})

const canSimulate = computed<boolean>(
  () =>
    isFull.value
    && currentRow.value?.status === 'pending'
    && rowProvider.value !== null
    && !!currentRow.value?.provider_charge_id
    && providerIsSimulableHere.value,
)

const simulateDescription = computed<string>(() =>
  t(
    'Dispara um webhook de teste para esta cobrança, pelo mesmo caminho de uma entrega real do gateway. Só existe em ambiente de desenvolvimento.',
  ),
)

/**
 * amount_brl chega em unidade MAIOR (já dividido pelo fator da moeda da linha),
 * e o gatilho quer centavos. Reconverte pelo mesmo fator para que o valor no
 * payload sintético seja exatamente o que está na tela — um valor errado aqui
 * não quebra a confirmação (o serviço usa a própria transação), mas mente na
 * trilha de auditoria, que é onde alguém vai olhar depois.
 */
const rowValueCents = computed<number | undefined>(() => {
  const major = currentRow.value?.amount_brl
  if (major == null) return undefined
  const currency = currentRow.value?.currency ?? 'BRL'
  // 'pt-BR' literal para espelhar EXATAMENTE a divisão feita em
  // StaffTransactionsPage::flattenToAttemptRows — o fator depende do par
  // (locale, moeda), então usar outro locale aqui faria a inversa não fechar.
  return Math.round(major * currencyFactor('pt-BR', currency))
})

async function handleSimulate(outcome: 'confirm' | 'reject'): Promise<void> {
  const row = currentRow.value
  const provider = rowProvider.value
  const chargeId = row?.provider_charge_id

  if (!row || !provider || !chargeId || isSimulating.value) return

  isSimulating.value = true
  simulateResult.value = null

  try {
    if (provider === 'asaas') {
      const dados = await fireAsaasTrigger(
        outcome === 'confirm' ? 'PAYMENT_CONFIRMED' : 'PAYMENT_OVERDUE',
        chargeId,
        rowValueCents.value,
      )

      // 'ignored' volta DENTRO de um envelope de sucesso: nada falhou, o evento
      // apenas não encontrou alvo. Reportar isso como sucesso ensinaria o
      // operador a confiar num botão que não fez nada.
      // O motivo e a via ficam FORA da string traduzida: ambos são códigos que
      // o backend emite (purchase_not_found, one_off), não prosa. Interpolar
      // dentro de t() os tornaria reféns de a chave existir no catálogo — uma
      // string nova renderiza o placeholder cru, e o operador leria
      // "não encontrou esta cobrança: {reason}", que não informa nada.
      if (dados.message === 'ignored') {
        simulateResult.value = {
          severity: 'warn',
          message:
            t('O gateway aceitou o evento, mas ele não encontrou esta cobrança:')
            + ' ' + (dados.reason ?? '—'),
        }
        return
      }

      simulateResult.value = {
        severity: 'success',
        message: t('Evento processado.') + ' (' + dados.lane + ')',
      }
    } else {
      const dados = await fireFakeTrigger(
        outcome === 'confirm' ? 'ONE_OFF_CHARGE_CONFIRMED' : 'ONE_OFF_CHARGE_FAILED',
        chargeId,
      )

      // MESMA regra da via do ASAAS acima, que faltava aqui: 'ignored' volta
      // DENTRO de um envelope de sucesso — nada falhou, o evento simplesmente
      // não confirmou nada — então throwOnEnvelopeFailure não dispara e o
      // await passa limpo. Anunciar isso como sucesso ensina o operador a
      // confiar num botão que não fez nada: na franquia 14 (2026-08-21) foram
      // dois cliques, dois webhooks 'ignored', a transação seguiu pendente e o
      // painel disse "Evento processado." nas duas vezes.
      // O motivo fica FORA do t() por ser código do backend, não prosa.
      if (dados.message === 'ignored') {
        simulateResult.value = {
          severity: 'warn',
          message:
            t('O gateway aceitou o evento, mas ele não confirmou nada:')
            + ' ' + (dados.reason ?? '—'),
        }
        return
      }

      simulateResult.value = { severity: 'success', message: t('Evento processado.') }
    }

    // A tabela atrás do modal precisa refletir a nova situação. O modal fica
    // ABERTO de propósito: o resultado acima é a única forma de saber o que o
    // disparo realmente fez, e fechar apagaria a resposta.
    eventBus.emit('staffTransaction.saved', { id: Number(row.id) })
  } catch (err) {
    log('StaffTransactionModal', 'handleSimulate', 'Erro ao simular webhook', {
      message: (err as Error)?.message,
      provider,
      outcome,
    })
    simulateResult.value = {
      severity: 'error',
      message: (err as Error)?.message?.trim() || t('Erro ao simular o webhook.'),
    }
  } finally {
    isSimulating.value = false
  }
}

async function handleMarkRefunded(): Promise<void> {
  const row = currentRow.value
  if (!row || row.id == null || marking.value) return

  marking.value = true
  try {
    await markRefunded(Number(row.id))
    // Optimistically reflect the new state in the still-open row object so a
    // reopened modal cannot show a stale 'Estorno pendente'; the page refresh
    // below replaces it with the server's own answer either way.
    currentRow.value = { ...row, status: 'refunded' }
    eventBus.emit('globalMessage.success', t('Transação marcada como estornada'))
    eventBus.emit('staffTransaction.saved', { id: Number(row.id) })
    closeModal()
  } catch (err) {
    // A refusal reaches here because the composable throws on the ENVELOPE
    // (§4, skill billing-staff-console). The modal stays OPEN and no success
    // message fires — the staff member must see that nothing happened.
    log('StaffTransactionModal', 'handleMarkRefunded', 'Erro ao marcar transação como estornada', err)
    eventBus.emit('globalMessage.error', t('Erro ao marcar a transação como estornada'))
  } finally {
    marking.value = false
  }
}

function onCopied(): void {
  eventBus.emit('globalMessage.success', t('ID copiado!'))
}

function onCopyFailed(): void {
  eventBus.emit('globalMessage.error', t('Não foi possível copiar'))
}

function onOpen(payload: AttemptDisplayRow | { row?: AttemptDisplayRow }): void {
  log('StaffTransactionModal', 'onOpen', 'Abrindo detalhe de transação', payload)
  const data = (payload && typeof payload === 'object' && 'row' in payload && payload.row)
    ? payload.row
    : (payload as AttemptDisplayRow)
  currentRow.value = data ?? null
  simulateResult.value = null
  isVisible.value = true


  // featureMap é estado SINGLETON de módulo, e apenas alguns componentes o
  // carregam. Um modal que só o LÊ enxerga null sempre que for a primeira tela
  // a precisar dele — e canSimulate() cairia para false, escondendo o botão sem
  // erro nenhum. Carregar aqui é o que torna o gate confiável.
  //
  // Só busca quando o mapa pode MUDAR ALGUMA COISA: cobrança pendente, de um
  // provedor simulável, para um Full. Sem esse recorte o modal dispararia um GET
  // a cada abertura — inclusive nas transações já confirmadas, que são a maioria
  // e onde o bloco nunca apareceria de qualquer forma.
  // Falha é não-fatal: sem mapa, o bloco simplesmente não aparece.
  if (isFull.value && currentRow.value?.status === 'pending' && rowProvider.value !== null) {
    loadGatewayMap().catch((err: unknown) => {
      log('StaffTransactionModal', 'onOpen', 'Erro ao carregar mapa de gateways', err)
    })
  }
}

function closeModal(): void {
  log('StaffTransactionModal', 'closeModal', 'Fechando detalhe de transação')
  isVisible.value = false
  currentRow.value = null
  simulateResult.value = null
  eventBus.emit('modal.staffTransaction.close')
}

onMounted(() => {
  eventBus.on('modal.staffTransaction.open', onOpen as (data?: unknown) => void)
})

onBeforeUnmount(() => {
  eventBus.off('modal.staffTransaction.open', onOpen as (data?: unknown) => void)
})
</script>

<style scoped>
.simulate-box {
  margin-top: 16px;
}

.simulate-description {
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-color-secondary, #6c757d);
  max-width: 68ch;
}

.simulate-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
</style>
